import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, ChevronUp, GripVertical, Plus, Trash2 } from "lucide-react";
import { PALETTE } from "@/lib/colors";

const Swatches = ({ value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {PALETTE.map((p, i) => (
      <button
        key={p.name}
        type="button"
        data-testid={`${testPrefix}-color-${i}`}
        onClick={() => onChange(i)}
        title={p.name}
        className="flex h-7 w-7 items-center justify-center rounded-full border"
        style={{ background: p.bg, borderColor: value === i ? p.accent : p.border, boxShadow: value === i ? `0 0 0 2px ${p.accent}` : "none" }}
      >
        {value === i && <Check className="h-3.5 w-3.5" style={{ color: p.ink }} />}
      </button>
    ))}
  </div>
);

const EmployeeEditor = ({ employee, onUpdate, onDelete, handle }) => {
  const [form, setForm] = useState(employee);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    setForm(employee);
  }, [employee]);
  const dirty = form.name !== employee.name || form.role !== employee.role || form.color !== employee.color;
  const p = PALETTE[form.color % PALETTE.length];

  return (
    <div data-testid={`employee-row-${employee.id}`} className="space-y-2 rounded-xl border p-3" style={{ background: p.soft, borderColor: p.border }}>
      <div className="flex gap-2">
        {handle}
        <Input data-testid={`employee-name-${employee.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-lg bg-white" />
        <Input data-testid={`employee-role-${employee.id}`} value={form.role} placeholder="Ruolo / mezzo" onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 rounded-lg bg-white" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Swatches value={form.color} onChange={(color) => setForm({ ...form, color })} testPrefix={`employee-${employee.id}`} />
        <div className="flex gap-1">
          <Button data-testid={`employee-delete-${employee.id}`} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => (confirm ? onDelete(employee.id) : setConfirm(true))}>
            <Trash2 className="h-4 w-4" /> {confirm && "Conferma"}
          </Button>
          <Button data-testid={`employee-save-${employee.id}`} size="sm" disabled={!dirty || !form.name.trim()} onClick={() => onUpdate(employee.id, form)}>Salva</Button>
        </div>
      </div>
    </div>
  );
};

const NewEmployee = ({ onCreate, nextColor }) => {
  const [form, setForm] = useState({ name: "", role: "", color: nextColor });
  useEffect(() => {
    setForm((f) => ({ ...f, color: nextColor }));
  }, [nextColor]);
  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim() && (await onCreate(form))) setForm({ name: "", role: "", color: nextColor });
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-xl border border-dashed p-3">
      <div className="flex gap-2">
        <Input data-testid="new-employee-name" value={form.name} placeholder="Nome e cognome" onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-lg" />
        <Input data-testid="new-employee-role" value={form.role} placeholder="Ruolo / mezzo" onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 rounded-lg" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Swatches value={form.color} onChange={(color) => setForm({ ...form, color })} testPrefix="new-employee" />
        <Button data-testid="new-employee-add-btn" type="submit" size="sm" disabled={!form.name.trim()} className="gap-1"><Plus className="h-4 w-4" /> Aggiungi</Button>
      </div>
    </form>
  );
};

export const EmployeesDialog = ({ open, onOpenChange, employees, onCreate, onUpdate, onDelete, onReorder }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="employees-dialog" className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-display">Dipendenti</DialogTitle>
        <DialogDescription>Ogni dipendente ha la sua riga colorata. Trascina la maniglia (o usa le frecce) per cambiare l'ordine nella griglia. Eliminandolo, le sue attività aperte tornano da assegnare.</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <SortableEmployees employees={employees} onUpdate={onUpdate} onDelete={onDelete} onReorder={onReorder} />
        <NewEmployee onCreate={onCreate} nextColor={employees.length % PALETTE.length} />
      </div>
    </DialogContent>
  </Dialog>
);

const moveId = (ids, dragId, targetId, before) => {
  const rest = ids.filter((x) => x !== dragId);
  rest.splice(rest.indexOf(targetId) + (before ? 0 : 1), 0, dragId);
  return rest;
};

const SortableEmployees = ({ employees, onUpdate, onDelete, onReorder }) => {
  const [ids, setIds] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [hint, setHint] = useState(null);
  useEffect(() => {
    setIds(employees.map((e) => e.id));
  }, [employees]);
  const byId = Object.fromEntries(employees.map((e) => [e.id, e]));
  const apply = async (next) => {
    setIds(next);
    if (!(await onReorder(next))) setIds(employees.map((e) => e.id));
  };
  const swap = (i, j) => {
    const next = [...ids];
    [next[i], next[j]] = [next[j], next[i]];
    apply(next);
  };

  return ids.filter((id) => byId[id]).map((id, i, list) => {
    let hintClass = "";
    if (hint?.id === id) hintClass = hint.before ? "drop-before" : "drop-after";
    return (
      <div
        key={id}
        className={`sortable-item ${hintClass} ${dragId === id ? "opacity-50" : ""}`}
        draggable={dragId === id}
        onDragStart={(e) => e.dataTransfer.setData("text/plain", id)}
        onDragEnd={() => {
          setDragId(null);
          setHint(null);
        }}
        onDragOver={(e) => {
          if (!dragId) return;
          e.preventDefault();
          const r = e.currentTarget.getBoundingClientRect();
          setHint({ id, before: e.clientY < r.top + r.height / 2 });
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (dragId && dragId !== id) apply(moveId(list, dragId, id, hint?.before ?? true));
          setDragId(null);
          setHint(null);
        }}
      >
        <EmployeeEditor
          employee={byId[id]}
          onUpdate={onUpdate}
          onDelete={onDelete}
          handle={
            <OrderControls
              id={id}
              onGrab={() => setDragId(id)}
              onRelease={() => setDragId(null)}
              up={i > 0 ? () => swap(i, i - 1) : null}
              down={i < list.length - 1 ? () => swap(i, i + 1) : null}
            />
          }
        />
      </div>
    );
  });
};

const OrderControls = ({ id, onGrab, onRelease, up, down }) => (
  <div className="flex shrink-0 items-center gap-1">
    <button
      type="button"
      data-testid={`employee-drag-${id}`}
      onMouseDown={onGrab}
      onMouseUp={onRelease}
      className="hidden h-10 w-7 cursor-grab items-center justify-center rounded-lg text-slate-400 hover:bg-white/70 active:cursor-grabbing md:flex"
      aria-label="Trascina per riordinare"
      title="Trascina per riordinare"
    >
      <GripVertical className="h-4 w-4" />
    </button>
    <div className="reorder-arrows !mt-0">
      <button type="button" data-testid={`employee-move-${id}-up`} disabled={!up} onClick={up || undefined} aria-label="Sposta su">
        <ChevronUp className="h-4 w-4" />
      </button>
      <button type="button" data-testid={`employee-move-${id}-down`} disabled={!down} onClick={down || undefined} aria-label="Sposta giù">
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  </div>
);
